import { useCallback, useEffect, useState } from "react";
import historicalOverridesService from "../../../../services/historicalOverridesService";
import {
  StockOverrideCreateInput,
  StockOverrideRecord,
  StockOverrideUpdateInput,
} from "../../../../types/historicalOverrides";

const PAGE_SIZE = 200;

export interface UseStockOverridesResult {
  overrides: StockOverrideRecord[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  create: (input: StockOverrideCreateInput) => Promise<StockOverrideRecord>;
  update: (
    id: number,
    input: StockOverrideUpdateInput
  ) => Promise<StockOverrideRecord>;
  remove: (id: number) => Promise<void>;
}

/**
 * When the modal is opened from the global overrides admin page, it is
 * passed the id of a specific record to edit. That id may live on any
 * page of the per-contract override list, so we accept an optional
 * `ensureVisibleId` and walk additional pages until the target is
 * loaded — without this, contracts with more than one page of overrides
 * silently drop back to create mode when the target is older than the
 * first page.
 */
export function useStockOverrides(
  conId: number,
  ensureVisibleId?: number
): UseStockOverridesResult {
  const [overrides, setOverrides] = useState<StockOverrideRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const first = await historicalOverridesService.listStockOverrides({
        conId,
        page: 1,
        pageSize: PAGE_SIZE,
      });
      let items: StockOverrideRecord[] = first.items ?? [];
      const totalPages = Math.max(1, first.totalPages ?? 1);

      if (
        ensureVisibleId !== undefined &&
        !items.some((o) => o.id === ensureVisibleId) &&
        totalPages > 1
      ) {
        for (let p = 2; p <= totalPages; p++) {
          const next = await historicalOverridesService.listStockOverrides({
            conId,
            page: p,
            pageSize: PAGE_SIZE,
          });
          items = items.concat(next.items ?? []);
          if (items.some((o) => o.id === ensureVisibleId)) break;
        }
      }

      setOverrides(items);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to load overrides";
      setError(msg);
      setOverrides([]);
    } finally {
      setIsLoading(false);
    }
  }, [conId, ensureVisibleId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Mutations must never surface a reload failure as a mutation failure:
  // the write already succeeded server-side, and propagating a refetch
  // error would trigger duplicate retries and spurious error toasts.
  const safeRefetch = useCallback(async () => {
    try {
      await refetch();
    } catch {
      /* refetch sets its own error state */
    }
  }, [refetch]);

  const create = useCallback(
    async (input: StockOverrideCreateInput) => {
      const resp = await historicalOverridesService.createStockOverride(input);
      await safeRefetch();
      return resp.override;
    },
    [safeRefetch]
  );

  const update = useCallback(
    async (id: number, input: StockOverrideUpdateInput) => {
      const resp = await historicalOverridesService.updateStockOverride(id, input);
      await safeRefetch();
      return resp.override;
    },
    [safeRefetch]
  );

  const remove = useCallback(
    async (id: number) => {
      await historicalOverridesService.deleteStockOverride(id);
      await safeRefetch();
    },
    [safeRefetch]
  );

  return { overrides, isLoading, error, refetch, create, update, remove };
}
